import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MoreVertical, Pin, PinOff, CheckCheck, LogOut } from "lucide-react";
import { api } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Quick actions for a Chats/Circles list row (v1.2.2 parity): pin/unpin, mark as read, and
// (for group Circles) leave — without opening the thread.
export function ChatRowActions({ chat, canLeave = false }) {
  const qc = useQueryClient();
  const rowKey = chat.type === "group" ? chat.id : chat.other?.username || chat.id;
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["chats"] });
    qc.invalidateQueries({ queryKey: ["bloqs"] });
  };

  const pinMut = useMutation({
    mutationFn: () => api.pinChat(chat.id),
    onSuccess: (res) => { invalidate(); toast.success(res?.pinned ? "Pinned to top" : "Unpinned"); },
    onError: () => toast.error("Couldn't update pin"),
  });
  const readMut = useMutation({
    mutationFn: () => api.markRead(chat.id),
    onSuccess: () => { invalidate(); toast.success("Marked as read"); },
    onError: () => toast.error("Couldn't mark as read"),
  });
  const leaveMut = useMutation({
    mutationFn: () => api.leaveCircle(chat.id),
    onSuccess: () => { invalidate(); toast.success("Left the Circle"); },
    onError: () => toast.error("Couldn't leave"),
  });

  const stop = (e) => e.stopPropagation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={stop}>
        <button
          type="button"
          className="flex h-7 w-7 items-center justify-center rounded-full text-bloq-muted opacity-0 transition-opacity hover:bg-white/10 hover:text-white focus:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
          data-testid={`chat-row-actions-${rowKey}`}
          title="Chat options"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={stop} className="border-white/12 bg-bloq-s1 text-white" data-testid={`chat-row-menu-${rowKey}`}>
        <DropdownMenuItem onClick={() => pinMut.mutate()} className="cursor-pointer gap-2 focus:bg-white/8" data-testid={`chat-action-pin-${rowKey}`}>
          {chat.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
          {chat.pinned ? "Unpin" : "Pin to top"}
        </DropdownMenuItem>
        {chat.unread > 0 && (
          <DropdownMenuItem onClick={() => readMut.mutate()} className="cursor-pointer gap-2 focus:bg-white/8" data-testid={`chat-action-read-${rowKey}`}>
            <CheckCheck className="h-4 w-4" /> Mark as read
          </DropdownMenuItem>
        )}
        {canLeave && (
          <DropdownMenuItem onClick={() => leaveMut.mutate()} className="cursor-pointer gap-2 text-destructive focus:bg-destructive/15 focus:text-destructive" data-testid={`chat-action-leave-${rowKey}`}>
            <LogOut className="h-4 w-4" /> Leave Circle
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
